import React, { useState, useEffect, useMemo } from "react";
import validator from "validator";
import { useForm } from "react-hook-form";
import { Combobox } from "@headlessui/react";
import colors from "../constants/colors";
import { BsInfoCircleFill } from "react-icons/bs";
import Swal from "sweetalert2";
import Loader from "./Loader";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  addOneYearToCurrentDate,
  formatDate,
} from "../utils/date_format";
import { stringToUpper, removeSpaces } from "../utils/data_transform";
import { useVehicleDetails } from "../store/VehicleStore";
import { useCompanyVehicleDetails } from "../store/CompanyVehicleStore";
import { useUserStore } from "../store/UserStore";
import { useOwnerStore } from "../store/OwnerStore";
import { useFormStep } from "../store/FormStepStore";
import VehicleInformationPreviewModal from "./VehicleInformationPreviewModal";
import { GrFormClose } from "react-icons/gr";
import {
  base,
  states as stateUrl,
  manufacturers as manufacturesUrl,
  models as modelsUrl,
  agent_nultiple_cmrinfo_requests as cmr_multiple_pre_entry,
  get_product_details,
  vehicle_types as v_type_url
} from "../constants/urls";
import { useAuth } from "../store/Auth";
import { useModal } from "../store/ModalState";
import { useProductStore } from "../store/ProductStore";
import { generate_trans_ref } from "../utils/generate_trans_ref";
import { useInvoiceStore } from "../store/InvoiceStore";
import { checkToken } from "./CheckToken";
import Header from "./assist/Title";

// Extracted static computations outside the component
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1980 + 1 }, (_, i) => currentYear - i);

export default function Vehicle() {
  const vehicleCount = localStorage.getItem("vehicleCount");
  
  const [showInfo, setShowInfo] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [states, setStates] = useState([]);
  const [vehicle_types, setVehicleTypes] = useState([]);
  const [vehicle_cat, setVehicleCat] = useState("");
  const [plateNumber, setPlateNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Global State
  const userDetails = useUserStore((state) => state.userDetails);
  const getProduct = useProductStore((state) => state.product);
  const setInvoice = useInvoiceStore((state) => state.setInvoiceDetails);
  const getAuth = useAuth((state) => state.auth);
  const ownerDetails = useOwnerStore((state) => state.ownerDetails);
  
  const vehicleDetails = useVehicleDetails((state) => state.vehicleDetails);
  const setVehicleDetails = useVehicleDetails((state) => state.setVehicleDetails);
  
  const multipleVehicleDetails = useCompanyVehicleDetails((state) => state.multipleVehicleDetails);
  const setMultipleVehicleDetails = useCompanyVehicleDetails((state) => state.setMultipleVehicleDetails);
  const clearAllItems = useCompanyVehicleDetails((state) => state.clearAllItems);
  const removeItem = useCompanyVehicleDetails((state) => state.removeItem);
  
  const step = useFormStep((state) => state.step);
  const setStep = useFormStep((state) => state.setStep);
  const setShowModal = useModal((state) => state.setShowModal);

  const {
    register,
    formState: { errors },
    reset,
    handleSubmit,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      license_plate: vehicleDetails.licencePlate || "",
      chassis_number: vehicleDetails.chassisNumber || "",
      engine_number: vehicleDetails.engineNumber || "",
      manufacturer: vehicleDetails.manufacturer || "",
      model: vehicleDetails.model || "",
      vehicle_type: vehicleDetails.vehicleType || "",
      year: vehicleDetails.year || "",
      vehicle_usage: vehicleDetails.vehicleUsage || "",
      color: vehicleDetails.color || "",
      source_of_vehicle: vehicleDetails.sourceOfVehicle || "",
      mla: vehicleDetails.mla || "",
      request_state: vehicleDetails.requestState || "",
    }
  });

  // Watch for dependent fields
  const selectedManufacturer = watch("manufacturer");

  // Sync to global state ONLY when moving forward
  const review = (data) => {
    setVehicleDetails({
      ...vehicleDetails,
      licencePlate: plateNumber === "new" ? null : data.license_plate,
      chassisNumber: data.chassis_number,
      engineNumber: data.engine_number,
      manufacturer: data.manufacturer,
      model: data.model,
      vehicleType: data.vehicle_type,
      year: data.year,
      vehicleUsage: data.vehicle_usage,
      color: data.color,
      sourceOfVehicle: data.source_of_vehicle,
      mla: data.mla,
      requestState: data.request_state,
    });
    setShowModal(true);
  };

  const handleMultipleVehicleRequest = (data) => {
    const newEntry = {
      licencePlate: plateNumber === "new" ? null : data.license_plate,
      chassisNumber: data.chassis_number,
      engineNumber: data.engine_number,
      manufacturer: data.manufacturer,
      model: data.model,
      vehicleType: data.vehicle_type,
      year: data.year,
      vehicleUsage: data.vehicle_usage,
      color: data.color,
      sourceOfVehicle: data.source_of_vehicle,
      mla: data.mla,
      requestState: data.request_state || vehicleDetails.requestState,
    };

    const sameLicence = multipleVehicleDetails.some((obj) => obj.licencePlate === newEntry.licencePlate);
    const sameChassis = multipleVehicleDetails.some((obj) => obj.chassisNumber === newEntry.chassisNumber);

    if (sameLicence && newEntry.licencePlate) {
      toast.error("Licence Plate Number entered already");
      return;
    } 
    
    if (sameChassis) {
      toast.error("Chassis Number entered already");
      return;
    }

    setMultipleVehicleDetails(newEntry);
    
    // Reset specific fields but maintain request_state for convenience
    reset({
      license_plate: "",
      chassis_number: "",
      engine_number: "",
      model: "",
      manufacturer: "",
      year: "",
      vehicle_type: "",
      color: "",
      vehicle_usage: "",
      mla: "",
      source_of_vehicle: "",
      request_state: data.request_state 
    });
  };

  const handleVehicleCategory = async (e) => {
    const category = e.target.value;
    setVehicleCat(category);
    
    try {
      const res = await fetch(base + v_type_url + category);
      const resJson = await res.json();
      if (resJson.statusCode === 0) setVehicleTypes(resJson.data || []);
    } catch (error) {
      console.error("Error fetching vehicle types:", error);
    }
  };

  const handlePlateNumberType = (e) => {
    setPlateNumber(e.target.value);
    if (e.target.value === "new") {
      setValue("license_plate", "");
    }
  };

  const addMultipleVehicles = async () => {
    setIsLoading(true);
    let token = getAuth.token;
    
    try {
      const prodRes = await fetch(base + get_product_details + getProduct, {
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      });
      const prodResp = await prodRes.json();
      const respData = prodResp.data;
      
      const referenceNumber = respData.product_code + generate_trans_ref().toString();
      const requestState = multipleVehicleDetails[0].requestState;
      
      const requestBody = {
        owner: {
          first_name: ownerDetails.firstName,
          middle_name: ownerDetails.middleName,
          last_name: ownerDetails.lastName,
          type: ownerDetails.profileType,
          gender: ownerDetails.gender,
          email: ownerDetails.email,
          phone: ownerDetails.phone,
          alternate_phone: "",
          state: ownerDetails.currentState,
          lga: ownerDetails.currentLG,
          registered_address: ownerDetails.pulledAddress,
          present_address: ownerDetails.address,
          id_type: ownerDetails.idType,
          id_number: ownerDetails.idNumber,
          pulled_state: ownerDetails.pulledState,
          pulled_lg: ownerDetails.pulledLG,
          status: "Verified",
        },
        pre_cmr: {
          reference: referenceNumber,
          entry_date: formatDate(new Date()),
          expiry_date: formatDate(addOneYearToCurrentDate()),
          amount: respData.price,
          payment_status: "Unpaid",
          status: "Pending",
          request_state: requestState,
        },
        vehicles: multipleVehicleDetails.map(v => ({
          license_plate: v.licencePlate,
          chassis_number: v.chassisNumber,
          engine_number: v.engineNumber,
          manufacturer: v.manufacturer,
          model: v.model,
          year: v.year,
          vehicle_type: v.vehicleType,
          vehicle_usage: v.vehicleUsage,
          vehicle_status: "Valid",
          mla: v.mla,
          color: v.color,
          mla_address: v.mlaAddress,
          vehicle_source: v.sourceOfVehicle,
        })),
      };

      const submitRes = await fetch(base + cmr_multiple_pre_entry, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify(requestBody),
      });
      const submitResp = await submitRes.json();

      if (submitResp.data === "vehicle exists") {
        Swal.fire({
          title: "Vehicles already registered",
          text: " ",
          icon: "success",
          confirmButtonText: "Ok",
          confirmButtonColor: "#1C89DE",
        }).then(() => {
          window.location = "/requests";
        });
        return;
      }

      const responseData = submitResp.data;
      const invoice = responseData.invoice;
      
      setInvoice({
        firstName: invoice.first_name,
        middleName: invoice.middle_name,
        lastName: invoice.last_name,
        address: invoice.address,
        email: invoice.email,
        phone: invoice.phone,
        narration: invoice.narration,
        amount: invoice.amount,
        transactionId: invoice.trans_ref,
        invoiceNumber: invoice.invoice_number,
        initiatedDate: formatDate(invoice.initiated_date),
        status: invoice.status,
        productCode: invoice.product_code,
        rrr: invoice.rrr,
        vin: invoice.vin,
        vat: invoice.vat,
        make: invoice.make,
        number_of_entry: responseData.number_of_entry,
        licenceNumber: invoice.license,
      });
      
      localStorage.setItem("vehicleEntriesCount", responseData.number_of_entry);
      setStep(6);
      window.location = "/invoice";
      
    } catch (error) {
      console.error("Error adding multiple vehicles:", error);
      toast.error("Failed to add vehicles. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setStep(5);
    window.location.href = "/owner";
  };

  // Resilient Fetch: States
  useEffect(() => {
    const controller = new AbortController();
    setVehicleDetails({});
    
    if (!validator.isURL(base + stateUrl)) return;
    
    fetch(base + stateUrl, { signal: controller.signal })
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson.statusCode === 0) setStates(resJson.data || []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error("Error fetching states:", error);
      });
      
    return () => controller.abort();
  }, [setVehicleDetails]);

  // Resilient Fetch: Manufacturers
  useEffect(() => {
    if (!vehicle_cat) return;
    const controller = new AbortController();
    
    fetch(base + manufacturesUrl + "/" + vehicle_cat, { signal: controller.signal })
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson.statusCode === 0) setManufacturers(resJson.data || []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error("Error fetching manufacturers:", error);
      });
      
    return () => controller.abort();
  }, [vehicle_cat]);

  // Resilient Fetch: Models
  useEffect(() => {
    if (!selectedManufacturer || !vehicle_cat) return;
    const controller = new AbortController();
    
    fetch(base + modelsUrl + selectedManufacturer + "/" + vehicle_cat, { signal: controller.signal })
      .then((res) => res.json())
      .then((resJson) => {
        if (resJson.statusCode === 0) setModels(resJson.data || []);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error("Error fetching models:", error);
      });
      
    return () => controller.abort();
  }, [selectedManufacturer, vehicle_cat]);

  useEffect(() => {
    if (getAuth.authenticated === true && getAuth.token !== "") {
      checkToken(getAuth.token).then((auth) => {
        if (!auth) {
          localStorage.clear();
          window.location = "/login";
        }
      });
    }
  }, [getAuth]);

  if (step < 5) {
    window.location = "/signup";
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />
      <Header />
      <div className="max-w-6xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 md:p-10 lg:p-12">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-6 animate-fade-in-down">
            Vehicle Details
          </h2>
          <form onSubmit={handleSubmit(vehicleCount === "multiple" ? handleMultipleVehicleRequest : review)} className="space-y-8">
            <div className="grid grid-cols-1 gap-6 animate-fade-in-up">
              {/* Vehicle Category and Plate Number Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Vehicle Category <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-4 sm:space-y-0">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="vcat"
                        value="motorvehicle"
                        onChange={handleVehicleCategory}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                      />
                      <span className="text-sm font-medium text-gray-700">Motor Vehicle</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="vcat"
                        value="motorcycle"
                        onChange={handleVehicleCategory}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                      />
                      <span className="text-sm font-medium text-gray-700">Motorcycle/Tricycle</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-2 relative">
                  <label className="block text-sm font-medium text-gray-700">
                    Plate Number Type <span className="text-red-500">*</span>
                    <BsInfoCircleFill
                      onClick={() => setShowInfo(!showInfo)}
                      className="inline-block ml-2 text-blue-500 hover:text-blue-700 cursor-pointer transition-colors duration-200 w-4 h-4"
                    />
                  </label>
                  {showInfo && (
                    <div className="absolute z-50 p-4 bg-white rounded-lg shadow-lg border border-gray-200 max-w-xs animate-fade-in">
                      <div className="flex justify-end">
                        <GrFormClose
                          onClick={() => setShowInfo(!showInfo)}
                          className="w-5 h-5 cursor-pointer text-gray-600 hover:text-gray-800"
                        />
                      </div>
                      <p className="text-sm text-gray-600">
                        <strong>Registered Plate Number:</strong> For vehicles with a Licence Plate Number.<br />
                        <strong>No Plate Number:</strong> For vehicles yet to be issued a Licence Plate Number.
                      </p>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-4 sm:space-y-0">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="plate"
                        value="old"
                        onChange={handlePlateNumberType}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                      />
                      <span className="text-sm font-medium text-gray-700">Registered Plate Number</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="plate"
                        value="new"
                        onChange={handlePlateNumberType}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 transition-colors duration-200"
                      />
                      <span className="text-sm font-medium text-gray-700">No Plate Number</span>
                    </label>
                  </div>
                </div>
              </div>

              {(plateNumber === "old" || plateNumber === "new") && (
                <div className="space-y-6">
                  {/* Licence Plate, Chassis, Engine */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {plateNumber === "old" && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Licence Plate Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          {...register("license_plate", {
                            required: "Licence Plate Number is required",
                            pattern: {
                              value: /^[a-zA-Z0-9]*$/,
                              message: "Invalid Licence Plate Number",
                            },
                            onChange: (e) => e.target.value = stringToUpper(removeSpaces(e.target.value))
                          })}
                          maxLength={8}
                          className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                          placeholder="e.g., ABC123XY"
                        />
                        {errors.license_plate && (
                          <p className="text-sm text-red-500 animate-pulse">{errors.license_plate.message}</p>
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        VIN/Chassis Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register("chassis_number", {
                          required: "Chassis Number is required",
                          pattern: {
                            value: /^[^IOQioq*,._]*$/,
                            message: "Invalid Chassis Number",
                          },
                          minLength: { value: 10, message: "Min 10 characters" },
                          maxLength: { value: 17, message: "Max 17 characters" },
                          onChange: (e) => e.target.value = stringToUpper(removeSpaces(e.target.value))
                        })}
                        maxLength={17}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                        placeholder="e.g., 1HGCM82633A004352"
                      />
                      {errors.chassis_number && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.chassis_number.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Engine Number
                      </label>
                      <input
                        type="text"
                        {...register("engine_number", {
                          onChange: (e) => e.target.value = stringToUpper(e.target.value)
                        })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base uppercase focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                        placeholder="e.g., A123456"
                      />
                    </div>
                  </div>

                  {/* Manufacturer, Model, Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Make <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("manufacturer", { 
                          required: "Manufacturer is required",
                          onChange: () => setValue("model", "") // Reset child select when parent changes
                        })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select Manufacturer</option>
                        {(manufacturers || []).map((val, index) => (
                          <option key={index} value={val}>{val}</option>
                        ))}
                      </select>
                      {errors.manufacturer && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.manufacturer.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Model <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("model", { required: "Model is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select Model</option>
                        {(models || []).map((val, index) => (
                          <option key={index} value={val}>{val}</option>
                        ))}
                      </select>
                      {errors.model && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.model.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("vehicle_type", { required: "Vehicle Type is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select Vehicle Type</option>
                        {(vehicle_types || []).map((val, index) => (
                          <option key={index} value={val}>{val}</option>
                        ))}
                      </select>
                      {errors.vehicle_type && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.vehicle_type.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Year, Usage, Color */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Year <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("year", { required: "Year is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Year of Manufacture</option>
                        {YEARS.map((val, index) => (
                          <option key={index} value={val}>{val}</option>
                        ))}
                      </select>
                      {errors.year && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.year.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Vehicle Usage <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("vehicle_usage", { required: "Vehicle Usage is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select Usage</option>
                        <option value="Private">Private</option>
                        <option value="Commercial">Commercial</option>
                        <option value="Government">Government</option>
                      </select>
                      {errors.vehicle_usage && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.vehicle_usage.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Color <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("color", { required: "Color is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select Color</option>
                        {(colors || []).map((val, index) => (
                          <option key={index} value={val}>{val}</option>
                        ))}
                      </select>
                      {errors.color && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.color.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Source, MLA, Request State */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Source of Vehicle <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("source_of_vehicle", { required: "Vehicle Source is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select Source</option>
                        <option value="Imported">Imported</option>
                        <option value="Locally Assembled">Locally Assembled</option>
                      </select>
                      {errors.source_of_vehicle && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.source_of_vehicle.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        State Licensing Office <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register("mla", { required: "MLA is required" })}
                        className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                      >
                        <option value="">Select State</option>
                        {(states || []).map((val, index) => (
                          <option key={index} value={val}>{val}</option>
                        ))}
                      </select>
                      {errors.mla && (
                        <p className="text-sm text-red-500 animate-pulse">{errors.mla.message}</p>
                      )}
                    </div>
                    
                    {multipleVehicleDetails.length < 1 && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          State Requesting From <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register("request_state", { required: "State of request is required" })}
                          className="w-full rounded-lg border-2 border-gray-200 bg-white p-3 text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-blue-300"
                        >
                          <option value="">Select State</option>
                          {(states || []).map((val, index) => (
                            <option key={index} value={val}>{val}</option>
                          ))}
                        </select>
                        {errors.request_state && (
                          <p className="text-sm text-red-500 animate-pulse">{errors.request_state.message}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={goBack}
                  className="w-full sm:w-32 bg-gray-500 text-white rounded-lg py-3 px-4 hover:bg-gray-600 transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Previous
                </button>
                <div className="flex space-x-4">
                  {vehicleCount === "multiple" ? (
                    <>
                      <button
                        type="submit"
                        className="w-full sm:w-32 bg-blue-500 text-white rounded-lg py-3 px-4 hover:bg-blue-600 transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
                      >
                        Add +
                      </button>
                      <button
                        type="button"
                        onClick={addMultipleVehicles}
                        disabled={multipleVehicleDetails.length === 0}
                        className="w-full sm:w-32 bg-blue-500 text-white rounded-lg py-3 px-4 hover:bg-blue-600 transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? <Loader /> : "Proceed >>"}
                      </button>
                    </>
                  ) : (
                    <button
                      type="submit"
                      className="w-full sm:w-32 bg-blue-500 text-white rounded-lg py-3 px-4 hover:bg-blue-600 transition-all duration-200 transform hover:scale-105 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
                    >
                      {isLoading ? <Loader /> : "Next >>"}
                    </button>
                  )}
                </div>
              </div>

              {/* Vehicle Table for Multiple Vehicles */}
              {vehicleCount === "multiple" && multipleVehicleDetails.length > 0 && (
                <div className="mt-8 overflow-x-auto">
                  <table className="w-full border-collapse bg-white shadow-md rounded-lg">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">S/No.</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Licence Plate</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Chassis Number</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Engine Number</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Manufacturer</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Model</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Vehicle Type</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Year</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Vehicle Usage</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Color</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Source</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Request State</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">MLA</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {multipleVehicleDetails.map((entry, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 transition-colors duration-200">
                          <td className="p-3 text-sm text-gray-600">{index + 1}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.licencePlate || '-'}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.chassisNumber}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.engineNumber || '-'}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.manufacturer}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.model}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.vehicleType}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.year}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.vehicleUsage}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.color}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.sourceOfVehicle}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.requestState}</td>
                          <td className="p-3 text-sm text-gray-600">{entry.mla}</td>
                          <td className="p-3">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="bg-red-500 text-white rounded-lg py-2 px-3 hover:bg-red-600 transition-all duration-200 transform hover:scale-105"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={clearAllItems}
                      className="bg-red-500 text-white rounded-lg py-3 px-4 hover:bg-red-600 transition-all duration-200 transform hover:scale-105"
                    >
                      Reset All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>
          <VehicleInformationPreviewModal title="CMR Motor Vehicle Information Request Review" />
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in-down { animation: fadeInDown 0.5s ease-out; }
        .animate-fade-in { animation: fadeIn 0.5s ease-out; }
        .animate-pulse { animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
      `}</style>
    </div>
  );
}